// TicketList.jsx
import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  TextField,
  IconButton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DataTable from "examples/Tables/DataTable";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import axios from "axios";
import { Notify } from "notiflix";
import { ENDPOINT } from "constants";
import { useSelector } from "react-redux";
import TicketDetail from "./TicketDetail";

const API_BASE = `${ENDPOINT}api/v1/admin/tickets`;

const TicketList = () => {
  const { user } = useSelector((state) => state.auth);
  const token = user?.accessToken;

  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);

  const [filters, setFilters] = useState({
    status: "",
    priority: "",
    category: "",
    user: "",
    sort: "newest",
    page: 1,
    search: "",
  });

  const [selectedTicket, setSelectedTicket] = useState(null);

  const fetchTickets = useCallback(async () => {
    try {
      setLoading(true);

      // Decide API endpoint: default vs search
      const url = filters.search ? `${API_BASE}/search` : API_BASE;

      const { data } = await axios.get(url, {
        params: filters,
        headers: { Authorization: `Bearer ${token}` },
      });

      if (data?.success) {
        setTickets(data.message || []);
      } else {
        Notify.failure(data?.message || "Failed to fetch tickets");
      }
    } catch (err) {
      console.error(err);
      Notify.failure(err?.response?.data?.message || "Error fetching tickets");
    } finally {
      setLoading(false);
    }
  }, [filters, token]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const handleSearchChange = (e) => {
    setFilters({ ...filters, search: e.target.value, page: 1 });
  };

  const columns = [
    { Header: "#", accessor: "id" },
    { Header: "Subject", accessor: "subject" },
    { Header: "Ticket Type", accessor: "ticketType" },
    { Header: "Status", accessor: "status" },
    { Header: "User", accessor: "user" },
    { Header: "Created", accessor: "createdAt" },
    {
      Header: "Actions",
      accessor: "actions",
      align: "center",
    },
  ];

  const rows = tickets.map((t, index) => ({
    id: index + 1,
    subject: t.subject,
    ticketType: t.ticketType || t.category?.name || "-",
    status: { open: "Open", in_progress: "Active", pending: "Processing", resolved: "Resolved", closed: "Resolved" }[t.status] || t.status,
    user: t.user?.email || "-",
    createdAt: new Date(t.createdAt).toLocaleString(),
    actions: (
      <MDButton
        size="small"
        variant="outlined"
        color="info"
        onClick={() => setSelectedTicket(t)}
      >
        View
      </MDButton>
    ),
  }));

  // If a ticket is selected, show detail page
  if (selectedTicket) {
    return (
      <TicketDetail
        ticket={selectedTicket}
        onBack={() => setSelectedTicket(null)}
      />
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Card>
          {/* Header */}
          <MDBox
            mx={2}
            mt={-3}
            py={3}
            px={2}
            variant="gradient"
            bgColor="info"
            borderRadius="lg"
            coloredShadow="info"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <MDTypography variant="h6" color="white">
              Support Tickets
            </MDTypography>
          </MDBox>

          {/* Filters & Search */}
          <MDBox
            p={2}
            display="flex"
            gap={2}
            flexWrap="wrap"
            alignItems="center"
          >
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                onChange={(e) =>
                  setFilters({ ...filters, status: e.target.value })
                }
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="open">Open</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="closed">Closed</MenuItem>
              </Select>
            </FormControl>

            <TextField
              size="small"
              placeholder="Search tickets..."
              value={filters.search}
              onChange={handleSearchChange}
              InputProps={{
                endAdornment: (
                  <IconButton onClick={fetchTickets}>
                    <SearchIcon />
                  </IconButton>
                ),
              }}
            />
          </MDBox>

          {/* Table */}
          <MDBox p={3}>
            {loading ? (
              <CircularProgress />
            ) : (
              <DataTable
                table={{ columns, rows }}
                isSorted={false}
                entriesPerPage={false}
                showTotalEntries={false}
                canSearch={false} // disable DataTable built-in search
              />
            )}
          </MDBox>
        </Card>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
};

export default TicketList;
